/**
 * IntegrationsPage — connect external services to Grafty
 *
 * Organized into four categories:
 *   Irrigation Controllers   — OpenSprinkler, Hunter HPC400, Rachio, Rain Bird, bHyve, Toro, Hydrawise
 *   Weather                 — Weather Underground (PWS), NOAA, OpenWeatherMap
 *   Commerce & Payments     — Stripe, Shopify, Square
 *   AI & Mapping            — Gemini (BYOK), Google Maps (BYOK)
 *
 * Each integration card shows:
 *   - Name + logo/icon
 *   - Status badge (connected / not connected / error)
 *   - Brief description
 *   - Configure button → opens modal with required fields
 *   - Plan badge (which plan includes this)
 *
 * Credentials are NEVER stored in the UI — they are POSTed to the API
 * which stores them in GCP Secret Manager.
 */

import { useState } from "react";
import { apiError } from '../lib/errors'
import { api, irrigationApi, paymentsApi } from "../lib/api";

// How each catalog card connects to a REAL backend (§207). Cards without a real
// per-tenant backend are "managed" (configured by Grafty via env/Secret Manager,
// or handled on another page) and show an info note instead of a broken form.
type IntegrationKind = "controller" | "bhyve" | "stripe" | "managed";
function integrationKind(id: string): IntegrationKind {
  if (id === "bhyve") return "bhyve";
  if (id === "stripe") return "stripe";
  // Irrigation controllers with a working /irrigation/controllers backend (§189).
  if (["hunter_hpc400", "opensprinkler", "rachio", "rainbird", "hydrawise"].includes(id)) return "controller";
  return "managed"; // weather, gemini, maps, shopify, toro_tempus
}
// What to tell the user for cards Grafty manages / aren't per-tenant API config.
const MANAGED_NOTE: Record<string, string> = {
  weather_underground: "Weather data is configured by Grafty for your farm's region (NWS + fallback). To wire your own PWS, contact support.",
  openweathermap: "Weather data is configured by Grafty for your farm's region. No setup needed.",
  gemini: "AI is managed in Settings → AI (enable features, set a budget, or connect your own GCP project).",
  google_maps: "Maps and satellite imagery are provided by Grafty — no setup needed.",
  shopify: "Shopify storefront sync is coming soon. For now, use the built-in Grafty storefront under Commerce.",
  toro_tempus: "Toro Tempus has no third-party API to connect to — manage it in the Toro app.",
};

// ── Types ─────────────────────────────────────────────────────────────────────

type Status = "connected" | "disconnected" | "error" | "pending";
type Plan = "seedling" | "grower" | "estate" | "all";

interface Integration {
  id: string;
  category: string;
  name: string;
  logo: string;
  description: string;
  status: Status;
  plan: Plan;
  docsURL?: string;
  fields: ConfigField[];
  connectedSince?: string;
  lastChecked?: string;
  errorMsg?: string;
}

interface ConfigField {
  key: string;
  label: string;
  type: "text" | "password" | "url" | "number" | "select";
  placeholder?: string;
  hint?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

// ── Integration definitions ───────────────────────────────────────────────────

const INTEGRATIONS: Integration[] = [
  // ── Irrigation controllers ────────────────────────────────────────────────
  {
    id: "hunter_hpc400",
    category: "Irrigation Controllers",
    name: "Hunter HPC400",
    logo: "💧",
    description: "Commercial Pro-C series controller. Connect via local network for direct zone control, program management, and real-time status.",
    status: "disconnected",
    plan: "grower",
    docsURL: "https://grafty.io/docs/integrations/hunter-hpc400",
    fields: [
      { key: "base_url", label: "Controller URL", type: "url", placeholder: "http://192.168.1.45", hint: "Local IP address of your HPC400 on your network. Use a DHCP reservation.", required: true },
      { key: "username", label: "Username", type: "text", placeholder: "admin", hint: "Default is 'admin'. Change this immediately after setup." },
      { key: "password", label: "Password", type: "password", placeholder: "••••••••", hint: "Change from the factory default before connecting.", required: true },
    ],
  },
  {
    id: "opensprinkler",
    category: "Irrigation Controllers",
    name: "OpenSprinkler",
    logo: "🌊",
    description: "Open-source irrigation controller. Full zone control, program scheduling, and live status via local network API.",
    status: "disconnected",
    plan: "grower",
    fields: [
      { key: "base_url", label: "Controller URL", type: "url", placeholder: "http://192.168.1.100", required: true },
      { key: "password", label: "Password (MD5)", type: "password", placeholder: "••••••••", hint: "Your OpenSprinkler password (stored as MD5).", required: true },
    ],
  },
  {
    id: "rachio",
    category: "Irrigation Controllers",
    name: "Rachio",
    logo: "☁️",
    description: "Cloud-based smart irrigation. Connect with an API key for zone control and schedule management.",
    status: "disconnected",
    plan: "grower",
    docsURL: "https://grafty.io/docs/integrations/rachio",
    fields: [
      { key: "api_key", label: "API Key", type: "password", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint: "Find this in the Rachio app → Account → API Access.", required: true },
      { key: "device_id", label: "Device ID", type: "text", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint: "Your controller's device ID from the Rachio API.", required: true },
    ],
  },
  {
    id: "bhyve",
    category: "Irrigation Controllers",
    name: "Orbit b·hyve",
    logo: "🔵",
    description: "b·hyve smart timer. Connects via your Orbit account credentials — all controllers and zones are auto-discovered.",
    status: "disconnected",
    plan: "grower",
    fields: [], // bHyve uses a custom two-step modal (BHyveConnectModal)
  },
  {
    id: "rainbird",
    category: "Irrigation Controllers",
    name: "Rain Bird (LNK WiFi)",
    logo: "🌧️",
    description: "Rain Bird LNK WiFi module — local control over your network (beta). From the cloud, reach it through a Cloudflare Tunnel; set that up on the Irrigation page.",
    status: "disconnected",
    plan: "grower",
    fields: [
      { key: "base_url", label: "Controller URL", type: "url", placeholder: "http://192.168.1.60", hint: "Local IP of the LNK module (or a tunnelled HTTPS URL).", required: true },
      { key: "password", label: "LNK Password", type: "password", placeholder: "••••••••", required: true },
    ],
  },
  {
    id: "toro_tempus",
    category: "Irrigation Controllers",
    name: "Toro Tempus",
    logo: "🟢",
    description: "Toro Tempus ELEMENT professional controller via Toro Connected platform.",
    status: "disconnected",
    plan: "estate",
    fields: [
      { key: "api_key", label: "Toro API Key", type: "password", required: true },
      { key: "device_id", label: "Controller Serial", type: "text", required: true },
    ],
  },
  {
    id: "hydrawise",
    category: "Irrigation Controllers",
    name: "Hunter Hydrawise",
    logo: "🌐",
    description: "Hunter's cloud irrigation platform. If your HPC400 is linked to Hydrawise, use this for cloud-based control.",
    status: "disconnected",
    plan: "grower",
    docsURL: "https://grafty.io/docs/integrations/hydrawise",
    fields: [
      { key: "api_key", label: "Hydrawise API Key", type: "password", hint: "Hydrawise → Account → API Key", required: true },
    ],
  },

  // ── Weather ───────────────────────────────────────────────────────────────
  {
    id: "weather_underground",
    category: "Weather",
    name: "Weather Underground PWS",
    logo: "⛅",
    description: "Connect your personal weather station or a nearby WU station for hyperlocal weather data, evapotranspiration, and yield correlation.",
    status: "disconnected",
    plan: "grower",
    fields: [
      { key: "api_key", label: "WU API Key", type: "password", hint: "weather.com → Developer → API Keys", required: true },
      { key: "station_id", label: "Station ID", type: "text", placeholder: "KFLCAPEC12", hint: "Your PWS station ID or a nearby station.", required: true },
    ],
  },
  {
    id: "openweathermap",
    category: "Weather",
    name: "OpenWeatherMap",
    logo: "🌤️",
    description: "Free and paid weather data. Good alternative or supplement to Weather Underground.",
    status: "disconnected",
    plan: "all",
    fields: [
      { key: "api_key", label: "API Key", type: "password", required: true },
      { key: "location", label: "Location", type: "text", placeholder: "Cape Coral, FL", required: true },
    ],
  },

  // ── Commerce & Payments ───────────────────────────────────────────────────
  {
    id: "stripe",
    category: "Commerce & Payments",
    name: "Stripe",
    logo: "💳",
    description: "Accept payments for farm store orders, CSA subscriptions, and wholesale invoices. Stripe is the default payment processor.",
    status: "disconnected",
    plan: "grower",
    fields: [
      { key: "publishable_key", label: "Publishable Key", type: "text", placeholder: "pk_live_...", required: true },
      { key: "secret_key", label: "Secret Key", type: "password", placeholder: "sk_live_...", required: true },
      { key: "webhook_secret", label: "Webhook Secret", type: "password", placeholder: "whsec_...", hint: "Set up a webhook in Stripe Dashboard pointing to your Grafty store URL." },
    ],
  },
  {
    id: "shopify",
    category: "Commerce & Payments",
    name: "Shopify",
    logo: "🛍️",
    description: "Sync your Grafty farm store with a Shopify storefront. Products, inventory, and orders flow both ways.",
    status: "disconnected",
    plan: "estate",
    fields: [
      { key: "shop_domain", label: "Shop Domain", type: "text", placeholder: "yourshop.myshopify.com", required: true },
      { key: "access_token", label: "Admin API Token", type: "password", required: true },
    ],
  },

  // ── AI & Mapping ──────────────────────────────────────────────────────────
  {
    id: "gemini",
    category: "AI & Mapping",
    name: "Google Gemini (BYOK)",
    logo: "🤖",
    description: "Bring your own Gemini API key for AI-powered yield predictions, ripening analysis, and planting advice. Required for Estate AI features.",
    status: "disconnected",
    plan: "estate",
    docsURL: "https://grafty.io/docs/byok",
    fields: [
      { key: "api_key", label: "Gemini API Key", type: "password", placeholder: "AIzaSy...", hint: "Create at aistudio.google.com → Get API Key. Usage billed directly to your Google account.", required: true },
    ],
  },
  {
    id: "google_maps",
    category: "AI & Mapping",
    name: "Google Maps (BYOK)",
    logo: "🗺️",
    description: "Your own Maps API key for higher map usage, satellite imagery, and custom map layers. The free Grafty tier uses a shared key with lower rate limits.",
    status: "disconnected",
    plan: "grower",
    docsURL: "https://grafty.io/docs/byok",
    fields: [
      { key: "api_key", label: "Maps API Key", type: "password", placeholder: "AIzaSy...", hint: "Enable Maps JavaScript API, Geocoding API, and Satellite tiles in Google Cloud Console.", required: true },
    ],
  },
];

const CATEGORIES = ["Irrigation Controllers", "Weather", "Commerce & Payments", "AI & Mapping"];

const PLAN_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  all:      { label: "All plans",  color: "#9CA3AF", bg: "rgba(156,163,175,0.1)" },
  seedling: { label: "Seedling+",  color: "#9CA3AF", bg: "rgba(156,163,175,0.1)" },
  grower:   { label: "Grower+",    color: "#60A5FA", bg: "rgba(96,165,250,0.1)"  },
  estate:   { label: "Estate",     color: "var(--color-primary)", bg: "color-mix(in srgb, var(--color-primary) 10%, transparent)"  },
};

const STATUS_CFG: Record<Status, { label: string; color: string; dot: string }> = {
  connected:    { label: "Connected",     color: "#34D399", dot: "#34D399" },
  disconnected: { label: "Not connected", color: "#6B7280", dot: "#374151" },
  error:        { label: "Error",         color: "#F87171", dot: "#F87171" },
  pending:      { label: "Checking…",     color: "#FBBF24", dot: "#FBBF24" },
};

// ── Shared modal styles ───────────────────────────────────────────────────────

const MODAL_BG      = "var(--color-bg-card)";
const MODAL_RAISED  = "var(--color-bg-card-2)";
const MODAL_BORDER  = "var(--color-border)";
const MODAL_BORDER2 = "var(--color-border)";
const TEXT_PRIMARY  = "var(--color-text)";
const TEXT_SECONDARY = "var(--color-text-muted)";
const TEXT_TERTIARY  = "var(--color-text-faint)";

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
  zIndex: 300, display: "flex", alignItems: "center",
  justifyContent: "center", padding: 20,
};

const cardStyle: React.CSSProperties = {
  width: "100%", maxWidth: 540,
  background: MODAL_BG,
  border: `1px solid ${MODAL_BORDER}`,
  borderRadius: 18,
  boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
  overflow: "hidden",
  fontFamily: "var(--font-body, 'DM Sans', sans-serif)",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 12px", fontSize: 14, boxSizing: "border-box" as const,
  background: MODAL_RAISED,
  border: `1px solid ${MODAL_BORDER2}`,
  borderRadius: 8, color: TEXT_PRIMARY,
  fontFamily: "inherit", outline: "none",
};

function ModalHeader({ logo, name, description, onClose }: {
  logo: string; name: string; description: string; onClose: () => void
}) {
  return (
    <div style={{
      padding: "20px 24px 18px",
      borderBottom: `1px solid ${MODAL_BORDER}`,
      display: "flex", alignItems: "center", gap: 14,
    }}>
      <span style={{ fontSize: 32 }}>{logo}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: TEXT_PRIMARY }}>{name}</div>
        <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 2 }}>{description}</div>
      </div>
      <button onClick={onClose} aria-label="Close" style={{
        width: 30, height: 30, borderRadius: 8, border: "none",
        background: MODAL_RAISED, color: TEXT_TERTIARY,
        fontSize: 18, cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>×</button>
    </div>
  );
}

// ── Generic config modal ──────────────────────────────────────────────────────

function ConfigModal({ integration, onClose, onSaved }: {
  integration: Integration;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: string, value: string) => setValues(v => ({ ...v, [key]: value }));

  // Used only by irrigation-controller cards now (§207) — maps the brand's fields to
  // the real /irrigation/controllers payload and creates the controller, then imports
  // its zones. It then shows up on the Irrigation page.
  const handleSave = async () => {
    setLoading(true); setError(null);
    try {
      const v = values;
      const payload: Record<string, string> = { type: integration.id, name: integration.name };
      for (const k of ["base_url", "otc_token", "password", "api_key"]) {
        if (v[k]?.trim()) payload[k] = v[k].trim();
      }
      // Hunter uses a username (→ device_id); cloud brands (Rachio/Hydrawise) use device_id.
      const dev = (v.username ?? v.device_id ?? "").trim();
      if (dev) payload.device_id = dev;
      const res = await irrigationApi.addController(payload);
      try { await irrigationApi.importZones(res.data.id); } catch { /* best effort */ }
      onSaved();
    } catch (e) {
      setError(apiError(e, "Failed to connect controller."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={overlayStyle}>
      <div style={cardStyle}>
        <ModalHeader
          logo={integration.logo}
          name={integration.name}
          description={integration.description.split(".")[0] + "."}
          onClose={onClose}
        />

        <div style={{ padding: "20px 24px" }}>
          {integration.fields.map(field => (
            <div key={field.key} style={{ marginBottom: 16 }}>
              <label style={{
                display: "block", fontSize: 12, fontWeight: 600,
                color: TEXT_SECONDARY, textTransform: "uppercase",
                letterSpacing: "0.05em", marginBottom: 6,
              }}>
                {field.label}
                {field.required && <span style={{ color: "var(--color-primary)", marginLeft: 3 }}>*</span>}
              </label>
              {field.type === "select" ? (
                <select style={inputStyle} value={values[field.key] ?? ""}
                  onChange={e => set(field.key, e.target.value)}>
                  <option value="">Select…</option>
                  {field.options?.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input type={field.type} style={inputStyle}
                  placeholder={field.placeholder} value={values[field.key] ?? ""}
                  onChange={e => set(field.key, e.target.value)} />
              )}
              {field.hint && (
                <div style={{ fontSize: 11, color: TEXT_TERTIARY, marginTop: 4, lineHeight: 1.5 }}>
                  {field.hint}
                </div>
              )}
            </div>
          ))}

          {error && (
            <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16,
              background: "rgba(248,113,113,0.08)", color: "#F87171", border: "1px solid rgba(248,113,113,0.2)" }}>
              {error}
            </div>
          )}

          <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 20,
            background: "color-mix(in srgb, var(--color-primary) 6%, transparent)", color: TEXT_TERTIARY, border: "1px solid color-mix(in srgb, var(--color-primary) 15%, transparent)" }}>
            🔒 Credentials are AES-256 encrypted at rest and never logged or exposed. The controller appears on your Irrigation page after connecting.
          </div>

          {integration.docsURL && (
            <a href={integration.docsURL} target="_blank" rel="noopener"
              style={{ fontSize: 12, color: TEXT_TERTIARY, display: "block", marginBottom: 16 }}>
              View setup documentation →
            </a>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{
              flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
              border: `1px solid ${MODAL_BORDER2}`,
              background: "transparent", color: TEXT_TERTIARY,
              fontSize: 14, fontFamily: "inherit",
            }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={loading} style={{
              flex: 2, padding: "10px 0", borderRadius: 10, cursor: "pointer",
              border: "none",
              background: loading ? MODAL_RAISED : "var(--color-primary)",
              color: loading ? TEXT_TERTIARY : "#0E1209",
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
              opacity: loading ? 0.7 : 1,
            }}>
              {loading ? "Connecting…" : "Connect controller"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── bHyve two-step connect modal ──────────────────────────────────────────────

interface BHyveDevice {
  id: string;
  name: string;
  type: string;
  is_connected: boolean;
  num_stations: number;
  zones: Array<{ station: number; name: string; enabled: boolean }>;
}

function deviceTypeLabel(type: string): string {
  if (!type) return "Controller";
  if (type.includes("sprinkler")) return "Sprinkler Timer";
  if (type.includes("hose")) return "Hose Timer";
  if (type === "bridge") return "Bridge / Hub";
  return type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function BHyveConnectModal({ onClose, onSaved }: {
  onClose: () => void;
  onSaved: () => void;
}) {
  type Step = "credentials" | "devices" | "success";
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{ api_key: string; user_id: string } | null>(null);
  const [devices, setDevices] = useState<BHyveDevice[]>([]);
  const [importResult, setImportResult] = useState<{ controllers: number; zones: number } | null>(null);

  const handleConnect = async () => {
    if (!email || !password) { setError("Email and password are required."); return; }
    setLoading(true); setError(null);
    try {
      const resp = await api.post<{ api_key: string; user_id: string; devices: BHyveDevice[] }>(
        "/irrigation/bhyve/connect", { email, password }
      );
      setSession({ api_key: resp.data.api_key, user_id: resp.data.user_id });
      setDevices(resp.data.devices ?? []);
      setStep("devices");
    } catch (e) {
      setError(apiError(e, "Connection failed."));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!session) return;
    setLoading(true); setError(null);
    try {
      const importResp = await api.post<{ controllers: number; zones: number; ok: boolean }>(
        "/irrigation/bhyve/import", {
          api_key: session.api_key,
          user_id: session.user_id,
          email,
        }
      );
      setImportResult({
        controllers: importResp.data.controllers,
        zones: importResp.data.zones,
      });
      setStep("success");
    } catch (e) {
      setError(apiError(e, "Import failed."));
    } finally {
      setLoading(false);
    }
  };

  const sprinklerDevices = devices.filter(d => d.type !== "bridge" && d.num_stations > 0);
  const bridgeDevices = devices.filter(d => d.type === "bridge" || d.num_stations === 0);

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={overlayStyle}>
      <div style={{ ...cardStyle, maxWidth: step === "devices" ? 600 : 540 }}>
        <ModalHeader
          logo="🔵"
          name="Orbit b·hyve"
          description={
            step === "credentials" ? "Sign in with your Orbit account to auto-discover all devices." :
            step === "devices"     ? `Found ${devices.length} device${devices.length !== 1 ? "s" : ""} on your account.` :
            "Successfully connected to Orbit b·hyve."
          }
          onClose={onClose}
        />

        <div style={{ padding: "20px 24px" }}>

          {/* ── Step 1: Credentials ───────────────────────────────────────── */}
          {step === "credentials" && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600,
                  color: TEXT_SECONDARY, textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 6 }}>
                  Orbit Email <span style={{ color: "var(--color-primary)" }}>*</span>
                </label>
                <input type="email" style={inputStyle}
                  placeholder="you@example.com" value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleConnect()} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600,
                  color: TEXT_SECONDARY, textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 6 }}>
                  Orbit Password <span style={{ color: "var(--color-primary)" }}>*</span>
                </label>
                <input type="password" style={inputStyle}
                  placeholder="••••••••" value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleConnect()} />
                <div style={{ fontSize: 11, color: TEXT_TERTIARY, marginTop: 4, lineHeight: 1.5 }}>
                  Same email and password you use in the Orbit b·hyve app. Your credentials are
                  stored encrypted in GCP Secret Manager — never logged or exposed.
                </div>
              </div>

              {error && (
                <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16,
                  background: "rgba(248,113,113,0.08)", color: "#F87171",
                  border: "1px solid rgba(248,113,113,0.2)" }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={onClose} style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${MODAL_BORDER2}`,
                  background: "transparent", color: TEXT_TERTIARY,
                  fontSize: 14, fontFamily: "inherit",
                }}>Cancel</button>
                <button onClick={handleConnect} disabled={loading} style={{
                  flex: 3, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                  border: "none", background: loading ? MODAL_RAISED : "var(--color-primary)",
                  color: loading ? TEXT_TERTIARY : "#0E1209",
                  fontSize: 14, fontWeight: 600, fontFamily: "inherit",
                  opacity: loading ? 0.7 : 1,
                }}>
                  {loading ? "Connecting…" : "Connect & Discover Devices"}
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: Discovered devices ────────────────────────────────── */}
          {step === "devices" && (
            <>
              {sprinklerDevices.length === 0 ? (
                <div style={{ padding: "20px 0", textAlign: "center", color: TEXT_TERTIARY, fontSize: 14 }}>
                  No sprinkler controllers found on this account. Make sure your devices are set up in the Orbit app.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                  {sprinklerDevices.map(dev => (
                    <div key={dev.id} style={{
                      padding: "12px 14px", borderRadius: 10,
                      background: MODAL_RAISED, border: `1px solid ${MODAL_BORDER}`,
                      display: "flex", alignItems: "flex-start", gap: 12,
                    }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 8, fontSize: 18,
                        background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>💧</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: TEXT_PRIMARY }}>{dev.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, color: TEXT_TERTIARY }}>{deviceTypeLabel(dev.type)}</span>
                          <span style={{ fontSize: 11, color: TEXT_TERTIARY }}>·</span>
                          <span style={{ fontSize: 11, color: TEXT_TERTIARY }}>
                            {dev.zones?.length ?? dev.num_stations} zone{(dev.zones?.length ?? dev.num_stations) !== 1 ? "s" : ""}
                          </span>
                          <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 600,
                            background: dev.is_connected ? "rgba(52,211,153,0.1)" : "rgba(107,114,128,0.15)",
                            color: dev.is_connected ? "#34D399" : "#6B7280",
                          }}>
                            {dev.is_connected ? "Online" : "Offline"}
                          </span>
                        </div>
                        {dev.zones && dev.zones.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                            {dev.zones.map(z => (
                              <span key={z.station} style={{
                                fontSize: 11, padding: "2px 8px", borderRadius: 12,
                                background: z.enabled ? "rgba(52,211,153,0.08)" : "rgba(107,114,128,0.1)",
                                color: z.enabled ? "#34D399" : TEXT_TERTIARY,
                                border: `1px solid ${z.enabled ? "rgba(52,211,153,0.2)" : MODAL_BORDER}`,
                              }}>
                                Zone {z.station}{z.name ? ` · ${z.name}` : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {bridgeDevices.length > 0 && (
                    <div style={{ fontSize: 12, color: TEXT_TERTIARY, padding: "6px 0" }}>
                      {bridgeDevices.length} bridge/hub device{bridgeDevices.length !== 1 ? "s" : ""} detected but not imported (no watering zones).
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16,
                  background: "rgba(248,113,113,0.08)", color: "#F87171",
                  border: "1px solid rgba(248,113,113,0.2)" }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setStep("credentials"); setError(null); }} style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${MODAL_BORDER2}`,
                  background: "transparent", color: TEXT_TERTIARY,
                  fontSize: 14, fontFamily: "inherit",
                }}>← Back</button>
                <button onClick={onClose} style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${MODAL_BORDER2}`,
                  background: "transparent", color: TEXT_TERTIARY,
                  fontSize: 14, fontFamily: "inherit",
                }}>Cancel</button>
                <button
                  onClick={handleImport}
                  disabled={loading || sprinklerDevices.length === 0}
                  style={{
                    flex: 3, padding: "10px 0", borderRadius: 10, cursor: sprinklerDevices.length === 0 ? "not-allowed" : "pointer",
                    border: "none",
                    background: (loading || sprinklerDevices.length === 0) ? MODAL_RAISED : "var(--color-primary)",
                    color: (loading || sprinklerDevices.length === 0) ? TEXT_TERTIARY : "#0E1209",
                    fontSize: 14, fontWeight: 600, fontFamily: "inherit",
                    opacity: loading ? 0.7 : 1,
                  }}>
                  {loading ? "Importing…" :
                    `Import All — ${sprinklerDevices.length} Controller${sprinklerDevices.length !== 1 ? "s" : ""}`}
                </button>
              </div>
            </>
          )}

          {/* ── Step 3: Success ───────────────────────────────────────────── */}
          {step === "success" && importResult && (
            <>
              <div style={{
                padding: "20px", borderRadius: 12, marginBottom: 20, textAlign: "center",
                background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)",
              }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: TEXT_PRIMARY, marginBottom: 6 }}>
                  b·hyve Connected!
                </div>
                <div style={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.6 }}>
                  Imported <strong style={{ color: TEXT_PRIMARY }}>{importResult.controllers}</strong> controller{importResult.controllers !== 1 ? "s" : ""} with <strong style={{ color: TEXT_PRIMARY }}>{importResult.zones}</strong> zones.
                  Head to <strong style={{ color: TEXT_PRIMARY }}>Irrigation</strong> to place zones on your farm map and set up watering schedules.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={onSaved} style={{
                  flex: 1, padding: "12px 0", borderRadius: 10, cursor: "pointer",
                  border: "none", background: "var(--color-primary)", color: "#0E1209",
                  fontSize: 14, fontWeight: 600, fontFamily: "inherit",
                }}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Integration card ──────────────────────────────────────────────────────────

function IntegrationCard({ integration, onConfigure }: {
  integration: Integration;
  onConfigure: () => void;
}) {
  const status = STATUS_CFG[integration.status];
  const plan = PLAN_LABELS[integration.plan];
  const isConnected = integration.status === "connected";

  return (
    <div style={{
      background: "var(--color-bg-card, #141A0F)",
      border: `1px solid ${isConnected ? "rgba(52,211,153,0.2)" : "var(--color-border, rgba(255,255,255,0.08))"}`,
      borderRadius: 14, padding: "18px 20px",
      transition: "border-color 0.15s, box-shadow 0.15s",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      {/* Top row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, fontSize: 24,
          background: "var(--color-bg-raised, #1C2415)",
          border: "1px solid var(--color-border, rgba(255,255,255,0.08))",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {integration.logo}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: "var(--color-text-primary, #F0EDE4)" }}>
              {integration.name}
            </span>
            {integration.plan !== "all" && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                background: plan.bg, color: plan.color,
                textTransform: "uppercase", letterSpacing: "0.04em",
              }}>
                {plan.label}
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <div style={{
              width: 7, height: 7, borderRadius: "50%", background: status.dot,
              boxShadow: isConnected ? `0 0 6px ${status.dot}` : "none",
            }} />
            <span style={{ fontSize: 12, color: status.color }}>{status.label}</span>
            {integration.connectedSince && (
              <span style={{ fontSize: 11, color: "var(--color-text-tertiary, #6B6760)" }}>
                · since {integration.connectedSince}
              </span>
            )}
          </div>
        </div>
      </div>

      <p style={{ fontSize: 13, color: "var(--color-text-secondary, #A8A49C)", lineHeight: 1.6, margin: 0 }}>
        {integration.description}
      </p>

      {integration.errorMsg && (
        <div style={{ fontSize: 12, color: "#F87171", background: "rgba(248,113,113,0.08)", padding: "6px 10px", borderRadius: 6 }}>
          {integration.errorMsg}
        </div>
      )}

      <button onClick={onConfigure} style={{
        padding: "8px 0", borderRadius: 8, cursor: "pointer",
        border: `1px solid ${isConnected ? "rgba(255,255,255,0.1)" : "color-mix(in srgb, var(--color-primary) 40%, transparent)"}`,
        background: isConnected ? "var(--color-bg-raised, #1C2415)" : "color-mix(in srgb, var(--color-primary) 6%, transparent)",
        color: isConnected ? "var(--color-text-secondary, #A8A49C)" : "var(--color-primary)",
        fontSize: 13, fontWeight: 500,
        fontFamily: "var(--font-body, 'DM Sans', sans-serif)",
        transition: "all 0.15s",
      }}>
        {isConnected ? "Reconfigure" : "Connect"}
      </button>
    </div>
  );
}

// ── Stripe Connect modal ──────────────────────────────────────────────────────
// Storefront card payments use per-tenant Stripe Connect (§136) — onboarding is a
// hosted redirect, not an API-key field. Reuses /payments/connect.
function StripeConnectModal({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connect = async () => {
    setLoading(true); setError(null);
    try {
      const r = await paymentsApi.connect();
      if (r.data?.url) { window.location.href = r.data.url; return; }
      setError("Stripe isn't configured on this environment yet.");
    } catch (e) {
      setError(apiError(e, "Couldn't start Stripe onboarding."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={overlayStyle}>
      <div style={cardStyle}>
        <ModalHeader logo={integration.logo} name={integration.name} description="Accept card payments on your farm storefront." onClose={onClose} />
        <div style={{ padding: "20px 24px" }}>
          <p style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.6, marginTop: 0 }}>
            Connect a Stripe account to take card payments on your Grafty storefront. You'll be redirected to Stripe to finish onboarding, then sent back here. You can also manage this on the Commerce page.
          </p>
          {error && (
            <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, background: "rgba(248,113,113,0.08)", color: "#F87171", border: "1px solid rgba(248,113,113,0.2)" }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: "10px 0", borderRadius: 10, cursor: "pointer", border: `1px solid ${MODAL_BORDER2}`, background: "transparent", color: TEXT_TERTIARY, fontSize: 14, fontFamily: "inherit" }}>Cancel</button>
            <button onClick={connect} disabled={loading} style={{ flex: 2, padding: "10px 0", borderRadius: 10, cursor: "pointer", border: "none", background: loading ? MODAL_RAISED : "var(--color-primary)", color: loading ? TEXT_TERTIARY : "#0E1209", fontSize: 14, fontWeight: 600, fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
              {loading ? "Redirecting…" : "Connect Stripe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Managed-integration info modal ────────────────────────────────────────────
// For cards Grafty configures itself (weather/AI/maps) or that live on another page
// (Shopify) — no per-tenant credential form, just an explanation (§207).
function ManagedInfoModal({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  const note = MANAGED_NOTE[integration.id] ?? "This integration is managed by Grafty — no setup needed.";
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={overlayStyle}>
      <div style={cardStyle}>
        <ModalHeader logo={integration.logo} name={integration.name} description={integration.description.split(".")[0] + "."} onClose={onClose} />
        <div style={{ padding: "20px 24px" }}>
          <div style={{ padding: "12px 14px", borderRadius: 8, fontSize: 13, lineHeight: 1.6, marginBottom: 18, background: "color-mix(in srgb, var(--color-primary) 6%, transparent)", color: TEXT_SECONDARY, border: "1px solid color-mix(in srgb, var(--color-primary) 15%, transparent)" }}>
            {note}
          </div>
          {integration.docsURL && (
            <a href={integration.docsURL} target="_blank" rel="noopener" style={{ fontSize: 12, color: TEXT_TERTIARY, display: "block", marginBottom: 16 }}>View documentation →</a>
          )}
          <button onClick={onClose} style={{ width: "100%", padding: "10px 0", borderRadius: 10, cursor: "pointer", border: "none", background: "var(--color-primary)", color: "#0E1209", fontSize: 14, fontWeight: 600, fontFamily: "inherit" }}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const [configuring, setConfiguring] = useState<Integration | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>(INTEGRATIONS);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories = ["All", ...CATEGORIES];

  const filtered = activeCategory === "All"
    ? integrations
    : integrations.filter(i => i.category === activeCategory);

  const groupedByCategory = CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(i => i.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {} as Record<string, Integration[]>);

  const connectedCount = integrations.filter(i => i.status === "connected").length;

  const handleSaved = (id: string) => {
    setIntegrations(prev =>
      prev.map(i => i.id === id ? { ...i, status: "connected" as Status, connectedSince: new Date().toLocaleDateString() } : i)
    );
    setConfiguring(null);
  };

  return (
    <div style={{
      padding: "32px 40px", maxWidth: 1100, margin: "0 auto",
      fontFamily: "var(--font-body, 'DM Sans', sans-serif)",
      color: "var(--color-text-primary, #F0EDE4)",
    }}>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontFamily: "var(--font-display, 'Syne', sans-serif)",
          fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em",
          color: "var(--color-text-primary, #F0EDE4)", margin: "0 0 8px",
        }}>
          Integrations
        </h1>
        <p style={{ fontSize: 15, color: "var(--color-text-secondary, #A8A49C)", margin: 0 }}>
          Connect your controllers, weather stations, and external services to Grafty.
          {connectedCount > 0 && (
            <span style={{ color: "#34D399", marginLeft: 8 }}>
              {connectedCount} active {connectedCount === 1 ? "integration" : "integrations"}
            </span>
          )}
        </p>
      </div>

      {/* Category filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 32, flexWrap: "wrap" }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)} style={{
            padding: "7px 16px", borderRadius: 8, cursor: "pointer",
            border: "1px solid",
            borderColor: activeCategory === cat ? "color-mix(in srgb, var(--color-primary) 40%, transparent)" : "var(--color-border, rgba(255,255,255,0.08))",
            background: activeCategory === cat ? "color-mix(in srgb, var(--color-primary) 8%, transparent)" : "var(--color-bg-card, #141A0F)",
            color: activeCategory === cat ? "var(--color-primary)" : "var(--color-text-secondary, #A8A49C)",
            fontSize: 13, fontWeight: activeCategory === cat ? 600 : 400,
            fontFamily: "inherit", transition: "all 0.15s",
          }}>
            {cat}
          </button>
        ))}
      </div>

      {/* Grouped integrations */}
      {Object.entries(groupedByCategory).map(([category, items]) => (
        <div key={category} style={{ marginBottom: 40 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.08em", color: "var(--color-text-tertiary, #6B6760)",
            marginBottom: 16,
          }}>
            {category}
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 14,
          }}>
            {items.map(integration => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConfigure={() => setConfiguring(integration)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Note at bottom */}
      <div style={{
        marginTop: 16, padding: "14px 18px", borderRadius: 12, fontSize: 13,
        background: "var(--color-bg-card, #141A0F)",
        border: "1px solid var(--color-border, rgba(255,255,255,0.08))",
        color: "var(--color-text-tertiary, #6B6760)", lineHeight: 1.7,
      }}>
        <strong style={{ color: "var(--color-text-secondary, #A8A49C)" }}>Missing an integration?</strong>
        {" "}Use the feedback button to request it — we prioritize integrations
        that our farmers actually use. Custom integrations via the
        {" "}<a href="/docs/api" style={{ color: "var(--color-primary)" }}>Grafty REST API</a> are available on all plans.
      </div>

      {/* Modals — dispatch by how the card actually connects (§207). */}
      {configuring && integrationKind(configuring.id) === "bhyve" && (
        <BHyveConnectModal
          onClose={() => setConfiguring(null)}
          onSaved={() => handleSaved("bhyve")}
        />
      )}
      {configuring && integrationKind(configuring.id) === "controller" && (
        <ConfigModal
          integration={configuring}
          onClose={() => setConfiguring(null)}
          onSaved={() => handleSaved(configuring.id)}
        />
      )}
      {configuring && integrationKind(configuring.id) === "stripe" && (
        <StripeConnectModal integration={configuring} onClose={() => setConfiguring(null)} />
      )}
      {configuring && integrationKind(configuring.id) === "managed" && (
        <ManagedInfoModal integration={configuring} onClose={() => setConfiguring(null)} />
      )}
    </div>
  );
}
